<template>
  <v-autocomplete
    :value="value"
    :items="visibleSets"
    :loading="loading"
    item-text="name"
    item-value="_key"
    return-object
    :multiple="multiple"
    clearable
    outlined
    dense
    hide-details
    hide-no-data
    :label="label"
    class="app-select"
    autocomplete="off"
    @input="$emit('input', $event || null)"
  >
    <template v-if="multiple" #selection="{ item }">
      <CommonChip x-small close @click:close="removeSelectedSet(item)">{{ item.name }}</CommonChip>
    </template>
    <template #item="{ item }">
      <v-list-item-content class="saved-set-select__content">
        <v-list-item-title>{{ item.name }}</v-list-item-title>
      </v-list-item-content>
      <v-list-item-action class="saved-set-select__action">
        <CommonChip v-if="showCount" x-small>{{ getSetCountLabel(item) }}</CommonChip>
        <SavedSetTypeChip v-else x-small :set-class="item.class" />
      </v-list-item-action>
    </template>
  </v-autocomplete>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import { mapState } from 'vuex';

import { Nullable } from '@/utils/types';
import { ServiceFactory } from '@/services/ServiceFactory';
import { IDefaultFilters } from '@/bundles/Panels/interfaces';

import CommonChip from '@/bundles/Common/components/chips/CommonChip.vue';
import SavedSetTypeChip from '@/bundles/SavedSets/components/chips/SavedSetTypeChip.vue';

const FilterViewService = ServiceFactory.get('filter-view');

interface ISavedSetSelectData {
  sets: IDefaultFilters[];
  loading: boolean;
}

export default Vue.extend({
  name: 'SavedSetSelect',

  components: { CommonChip, SavedSetTypeChip },

  props: {
    source: {
      type: String,
      required: true,
    },
    // A plain set id is a valid value: v-select resolves the label out of `items` by item-value.
    value: {
      type: [Object, Array, String] as PropType<Nullable<IDefaultFilters> | IDefaultFilters[] | string>,
      default: null,
    },
    label: {
      type: String,
      default: 'Saved Set',
    },
    onlyStatic: Boolean,
    showCount: Boolean,
    multiple: Boolean,
    preselectId: {
      type: String as PropType<Nullable<string>>,
      default: null,
    },
  },

  data: (): ISavedSetSelectData => ({
    sets: [],
    loading: false,
  }),

  computed: {
    ...mapState({
      token: (state: any) => state.idToken,
    }),
    visibleSets (): IDefaultFilters[] {
      return this.onlyStatic ? this.sets.filter((set) => set.class === 'static') : this.sets;
    },
  },

  watch: {
    preselectId (next) {
      this.$emit('input', next);
      // An id arriving later (back/forward) may sit outside the fetched page — refetch to pull it in.
      if (next && !this.sets.some((set) => set._key === next)) {
        this.fetchSets();
      }
    },
  },

  created () {
    this.fetchSets();
  },

  mounted () {
    /* The id alone is enough for the parent to hold as the value: with_ids guarantees that set is
       in the response, and v-select resolves the label out of `items` by item-value. */
    if (this.preselectId) {
      this.$emit('input', this.preselectId);
    }
  },

  methods: {
    async fetchSets () {
      try {
        this.loading = true;
        const preselectId = this.preselectId;
        const params = [{ name: 'list', value: this.source }];
        if (preselectId) {
          params.push({ name: 'with_ids', value: preselectId });
        }
        const { data } = await FilterViewService.list({ token: this.token, params });
        this.sets = data.result;
        // with_ids returns the set whenever the account can open it, so a missing one (deleted, another account's key) is dropped.
        if (preselectId && preselectId === this.preselectId && !this.sets.some((set) => set._key === preselectId)) {
          this.$emit('input', null);
        }
      } catch (error: any) {
        this.addNotification(error);
      } finally {
        this.loading = false;
      }
    },
    getSetCountLabel (item: IDefaultFilters): string {
      const count = item.savedSet?.count?.length ?? 0;

      return `${count} records`;
    },
    removeSelectedSet (item: IDefaultFilters) {
      this.$emit('input', (this.value as IDefaultFilters[]).filter((set) => set._key !== item._key));
    },
  },
});
</script>

<style scoped>
.saved-set-select__content {
  min-width: 0;
}

.saved-set-select__action {
  flex-shrink: 0;
}
</style>
